<template>
  <div class="bpjs-page">
        <PageHeader icon="fas fa-id-card">
      <template #title>
        Verifikasi Peserta BPJS
      </template>
      <template #subtitle>
        Cari data kepesertaan aktif menggunakan NIK atau nomor kartu JKN.
      </template>
    </PageHeader>

    <div class="card card-custom gutter-b border-0 shadow-sm mb-5 bpjs-form-card">
      <div class="card-body">
        <b-form @submit.prevent="search">
          <div class="row align-items-start bpjs-filter-row participant-search-row">
            <div class="col-lg-10 col-md-8 bpjs-filter-column">
              <b-form-group label="NIK atau Nomor Kartu BPJS" label-class="font-weight-bolder" class="mb-0">
                <b-form-input
                  v-model.trim="number"
                  inputmode="numeric"
                  maxlength="16"
                  placeholder="Masukkan 13 digit nomor kartu atau 16 digit NIK"
                  class="form-control-lg bpjs-form-control"
                />
                <small class="form-text text-muted">
                  {{ identityHint }}
                </small>
              </b-form-group>
            </div>
            <div class="col-lg-2 col-md-4 bpjs-filter-column participant-search-action">
              <b-button type="submit" variant="primary" block :disabled="loading || !valid" class="bpjs-control-button bpjs-primary-action participant-search-button">
                <b-spinner v-if="loading" small class="mr-2" /><i v-else class="fas fa-search mr-2"></i>Cari Peserta
              </b-button>
            </div>
          </div>
        </b-form>
      </div>
    </div>

    <b-alert :show="Boolean(error)" variant="danger" dismissible @dismissed="error = ''">{{ error }}</b-alert>
    <b-card v-if="participant" class="bpjs-card">
      <div class="d-flex justify-content-between align-items-center mb-5">
        <div>
          <span class="badge badge-light-success mb-2">Data ditemukan</span>
          <h4 class="mb-0 font-weight-bolder">{{ participant.nama || participant.name || "Peserta BPJS" }}</h4>
        </div>
        <div class="d-flex align-items-center">
          <i class="fas fa-check-circle text-success fa-2x"></i>
        </div>
      </div>
      <div class="bpjs-result-grid">
        <div v-for="item in participantDetails" :key="item.key" class="bpjs-result-item">
          <small>{{ item.label }}</small>
          <div class="bpjs-result-value">
            <strong>{{ item.value }}</strong>
            <span v-if="item.code" class="bpjs-result-code">Kode {{ item.code }}</span>
          </div>
        </div>
      </div>
      <div v-if="participantActive" class="d-flex justify-content-end mt-5">
        <b-button
          variant="success"
          class="bpjs-control-button bpjs-primary-action px-6"
          @click="continueToQueue"
        >
          <i class="fas fa-arrow-right mr-2"></i>Lanjut ke Antrean JKN
        </b-button>
      </div>
      <b-alert v-else show variant="warning" class="mt-5 mb-0">
        Antrean JKN hanya dapat dibuat untuk peserta dengan status aktif.
      </b-alert>
    </b-card>
    <div v-else class="bpjs-empty">
      <i class="far fa-address-card fa-3x mb-3"></i>
      <div>Data peserta akan tampil di sini setelah pencarian.</div>
    </div>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { unwrap, errorMessage, displayValue } from "./bpjs.helpers";

export default {
  data: () => ({
    number: "",
    loading: false,
    participant: null,
    error: "",
  }),
  computed: {
    valid() { return /^\d{13}$|^\d{16}$/.test(this.number); },
    identityType() { return this.number.length === 16 ? "nik" : "nokartu"; },
    identityHint() {
      if (!this.number) return "Jenis identitas akan dikenali otomatis oleh sistem.";
      if (!/^\d+$/.test(this.number)) return "Gunakan angka saja tanpa spasi atau tanda baca.";
      if (this.number.length === 13) return "Terdeteksi sebagai Nomor Kartu BPJS.";
      if (this.number.length === 16) return "Terdeteksi sebagai NIK.";
      return `${this.number.length} digit dimasukkan — lengkapi menjadi 13 atau 16 digit.`;
    },
    participantActive() {
      const status = this.participant && this.participant.statusPeserta;
      const value = status && typeof status === "object"
        ? status.keterangan || status.nama || status.description || status.kode
        : status;
      return String(value || "").trim().toUpperCase() === "AKTIF"
        || String(value || "").trim() === "0";
    },
    participantDetails() {
      if (!this.participant || typeof this.participant !== "object") return [];
      return Object.entries(this.participant).map(([key, value]) => ({
        key,
        label: this.label(key),
        value: this.participantValue(value, key),
        code: this.participantCode(value),
      }));
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: "BPJS Kesehatan" }, { title: "Peserta" }]);
  },
  methods: {
    async search() {
      if (!this.valid) return;
      this.loading = true;
      this.error = "";
      this.participant = null;
      try {
        const response = await ApiService.get(`bpjs/peserta/${this.identityType}/${this.number}`);
        if (response.status >= 400) throw response;
        const value = unwrap(response);
        this.participant = value && value.peserta ? value.peserta : value;
        if (!this.participant || typeof this.participant !== "object") throw new Error("Data peserta tidak ditemukan.");
      } catch (error) {
        this.error = errorMessage(error, "Data peserta tidak ditemukan.");
      } finally {
        this.loading = false;
      }
    },
    continueToQueue() {
      if (!this.participantActive) return;

      const participant = this.participant || {};
      const transfer = {
        noKartu: String(participant.noKartu || "").replace(/\D/g, ""),
        nik: String(participant.nik || "").replace(/\D/g, ""),
        noHP: String(participant.noHP || participant.noHp || "").replace(/\D/g, ""),
        nama: participant.nama || participant.name || "Peserta BPJS",
        status: "AKTIF",
        verifiedAt: new Date().toISOString(),
      };

      sessionStorage.setItem("medhop.bpjs.verifiedParticipant", JSON.stringify(transfer));
      this.$router.push({ name: "bpjs-antrean", query: { from: "participant" } });
    },
    participantValue(value, key) {
      if (key === "sex" && typeof value === "string") {
        return { L: "Laki-laki", P: "Perempuan" }[value.toUpperCase()] || value;
      }
      if (!value || typeof value !== "object") return displayValue(value);
      if (Array.isArray(value)) {
        return value.map(item => this.participantValue(item, key)).join(", ") || "-";
      }

      const preferredKeys = [
        "keterangan", "nama", "name", "nmPPK", "nmProvider",
        "description", "deskripsi", "label", "text",
      ];
      const descriptiveKey = preferredKeys.find(item => value[item] !== undefined && value[item] !== null && value[item] !== "");
      if (descriptiveKey) return displayValue(value[descriptiveKey]);

      const readableValues = Object.values(value)
        .filter(item => item !== null && item !== undefined && typeof item !== "object")
        .map(displayValue);
      return readableValues.join(" • ") || "-";
    },
    participantCode(value) {
      if (!value || typeof value !== "object" || Array.isArray(value)) return "";
      const codeKeys = ["kode", "code", "kdPPK", "kdProvider"];
      const key = codeKeys.find(item => value[item] !== undefined && value[item] !== null && value[item] !== "");
      if (!key) return "";
      const code = String(value[key]);
      return this.participantValue(value).includes(code) ? "" : code;
    },
    label(key) {
      const labels = {
        noKartu: "Nomor Kartu BPJS",
        nik: "NIK",
        noHP: "Nomor HP",
        tglLahir: "Tanggal Lahir",
        statusPeserta: "Status Peserta",
        jenisPeserta: "Jenis Peserta",
        kelasTanggungan: "Kelas Tanggungan",
        ppk: "Fasilitas Kesehatan Tingkat I",
        sex: "Jenis Kelamin",
      };
      if (labels[key]) return labels[key];
      return key.replace(/([A-Z])/g, " $1").replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase());
    },
  },
};
</script>

<style lang="scss" scoped>
.participant-search-action {
  padding-top: 28px;
}

.participant-search-button {
  width: 100%;
  padding-right: 14px !important;
  padding-left: 14px !important;
  white-space: nowrap;
}

@media (max-width: 767.98px) {
  .participant-search-action {
    padding-top: 12px;
  }
}
</style>
